import React from 'react'
import { DocumentNode } from 'graphql'
import {
  ApolloError,
  ApolloClient,
  ObservableQuery,
  ApolloCurrentResult,
  ApolloQueryResult,
  FetchMoreOptions
} from 'apollo-client'
import memoize from 'lodash.memoize'
import { CollectionVariables } from '.'

interface LoadMoreProps extends FetchMoreOptions {
  readonly variables?: CollectionVariables
}

interface CollectionResult<TData> {
  readonly items: TData[]
  readonly total: number
  readonly globalTotal?: number
}

interface QueryResults<TData> {
  readonly data: CollectionResult<TData>
  readonly error?: ApolloError
  readonly loading: boolean
  readonly loadMore: () => Promise<any>
  readonly refetch: () => Promise<any>
}

interface Props<TData> {
  readonly query: DocumentNode
  readonly variables: CollectionVariables
  readonly areVariablesEqual?: (
    var1: CollectionVariables,
    var2: CollectionVariables
  ) => boolean
  readonly dataKey: string
  readonly client: ApolloClient<any>
  readonly children: (props: QueryResults<TData>) => React.ReactNode
}

interface State<TData> {
  readonly result: ApolloCurrentResult<TData>
  readonly loadMore: (props: LoadMoreProps) => Promise<any>
  readonly refetch: (
    variables: CollectionVariables
  ) => Promise<ApolloQueryResult<TData>>
}

export function areVariablesNotEqual<TData>(
  var1: CollectionVariables,
  var2: CollectionVariables,
  areVariablesEqual?: Props<TData>['areVariablesEqual']
) {
  return (
    Object.keys(var1).some((key) => {
      if (Array.isArray(var1[key])) {
        return (
          !Array.isArray(var2[key]) || var1[key].join('') !== var2[key].join('')
        )
      }
      if (key === 'orderBy') {
        return (
          var1.orderBy.direction !== var2.orderBy.direction ||
          var1.orderBy.field !== var2.orderBy.field
        )
      }
      return var1[key] !== var2[key]
    }) ||
    Object.keys(var1).length !== Object.keys(var2).length ||
    (areVariablesEqual && !areVariablesEqual(var1, var2))
  )
}

export class CollectionQuery<TData> extends React.Component<
  Props<TData>,
  State<TData>
> {
  private queryObservable: ObservableQuery<TData>
  private querySubscription
  private getLoadMoreMemoizedResult

  constructor(props) {
    super(props)

    const { query, variables } = this.props
    this.queryObservable = this.props.client.watchQuery({
      query,
      variables,
      fetchPolicy: 'network-only',
      errorPolicy: 'all'
    })
    this.getLoadMoreMemoizedResult = this.getLoadMoreResultMemoizer()

    this.state = this.getCurrentData()
  }

  public componentDidMount() {
    this.startQuerySubscription()
  }

  public componentWillUnmount() {
    this.removeQuerySubscription()
  }

  public render() {
    const { children } = this.props
    const queryResult = this.getQueryResult()
    return children(queryResult)
  }

  public componentDidUpdate(prevProps: Props<TData>) {
    if (this.areVariablesNotEqual(prevProps.variables, this.props.variables)) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      this.queryObservable.setVariables(this.props.variables, true)
      this.getLoadMoreMemoizedResult = this.getLoadMoreResultMemoizer()
    }
  }

  private areVariablesNotEqual = (
    var1: CollectionVariables,
    var2: CollectionVariables
  ) => areVariablesNotEqual(var1, var2, this.props.areVariablesEqual)

  private getQueryResult = (): QueryResults<TData> => {
    const { dataKey, variables } = this.props
    const { limit } = variables
    const {
      result: { loading, error, errors, data },
      loadMore
    } = this.state

    const collection = (data && data[dataKey]) || {}
    const { items = [] } = collection
    if (error) {
      throw new Error(
        !!error.message ? error.message : 'unknown error in collection query'
      )
    }
    if (Array.isArray(errors) && errors.length) {
      throw new Error(errors.map((e) => e.message).join(','))
    }
    return {
      data: collection,
      loading,
      error: errors ? new ApolloError({ graphQLErrors: errors }) : error,
      loadMore: () => {
        return this.getLoadMoreMemoizedResult(items.length)
      },
      refetch: async () => {
        const numberOfLoadedPages = Math.ceil(items.length / limit)
        const { variables } = this.props
        async function reloadPage(pageNumber) {
          let items: TData[] = []

          await loadMore({
            variables: Object.assign({}, variables, {
              offset: pageNumber * limit
            }),

            updateQuery(previous, { fetchMoreResult }) {
              items =
                fetchMoreResult &&
                fetchMoreResult[dataKey] &&
                fetchMoreResult[dataKey].items

              return previous
            }
          })

          return items
        }

        async function reloadPages(pageReloaders: Array<Promise<any>>) {
          const itemArrays = await Promise.all(pageReloaders)
          return [].concat(...itemArrays)
        }

        const pageReloaders: Array<Promise<any>> = []
        for (let i = 1; i < numberOfLoadedPages; i++) {
          pageReloaders.push(reloadPage(i))
        }

        const refreshedItems = pageReloaders
          ? await reloadPages(pageReloaders)
          : []

        return loadMore({
          variables,
          updateQuery(_previousData, { fetchMoreResult }) {
            const newResult = fetchMoreResult && fetchMoreResult[dataKey]

            return {
              [dataKey]: {
                ...newResult,
                items: [...newResult.items, ...refreshedItems]
              }
            }
          }
        })
      }
    }
  }

  private startQuerySubscription = () => {
    if (!this.querySubscription) {
      const current = this.getQueryResult()
      this.querySubscription = this.queryObservable.subscribe({
        next: () => {
          if (!current || current.loading) {
            this.updateCurrentData()
          }
        }
      })
    }
  }

  private removeQuerySubscription = () => {
    if (this.querySubscription) {
      this.querySubscription.unsubscribe()
      delete this.querySubscription
    }
  }

  private updateCurrentData = () => {
    this.setState(this.getCurrentData())
  }

  private getCurrentData = () => {
    return {
      result: this.queryObservable.currentResult(),
      loadMore: this.queryObservable.fetchMore.bind(this.queryObservable),
      refetch: this.queryObservable.refetch.bind(this.queryObservable)
    }
  }

  private getLoadMoreResultMemoizer = () => {
    return memoize((offset: number) => {
      const { variables, dataKey } = this.props
      const { loadMore } = this.state

      const newVariables = Object.assign({}, variables, {
        offset
      })

      return loadMore({
        variables: newVariables,
        updateQuery(
          { [dataKey]: previousItems, ...previousData },
          { fetchMoreResult }
        ) {
          const newItems = fetchMoreResult && fetchMoreResult[dataKey]

          return {
            ...previousData,
            [dataKey]: {
              ...previousItems,
              items: [...previousItems.items, ...newItems.items],
              total: newItems.total
            }
          }
        }
      })
    })
  }
}
